import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';

/**
 * PendientesAgente — el usuario confirma (o descarta) lo que su IA preparo.
 * El agente no confirma solo. Execute sigue cerrado: nada se mueve.
 */
const GATEWAY = axios.create({
  baseURL: import.meta.env.VITE_AGENT_GATEWAY_URL || 'https://api-beta-v2.flowexpay.com/agent-gateway',
  headers: { 'Content-Type': 'application/json' },
  timeout: 10000,
});
GATEWAY.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const fmtFecha = (d) => (d ? new Date(d).toLocaleString('es-CO', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');

function money(amount, currency) {
  const n = Number(amount || 0);
  const cur = String(currency || '').toUpperCase();
  if (cur === 'COP') {
    return `${n.toLocaleString('es-CO', { maximumFractionDigits: 0 })} COP`;
  }
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD`;
}

function detailsOf(item) {
  const preview = item.preview || {};
  const input = preview.input || {};
  const fee = preview.feeEstimado || {};
  if (item.kind === 'order') {
    const side = String(input.side || '').toLowerCase() === 'sell' ? 'venta' : 'compra';
    const symbol = input.symbol || '—';
    return {
      kindLabel: 'orden',
      monto: money(preview.notionalUsd != null ? preview.notionalUsd : input.notional, 'USD'),
      destino: `${side} · ${symbol}`,
      destinoLabel: 'Símbolo',
      fee: money(fee.amount, fee.currency || 'USD'),
    };
  }
  return {
    kindLabel: 'envío',
    monto: money(input.amount, input.currency || (preview.neto && preview.neto.currency)),
    destino: input.destinatario || '—',
    destinoLabel: 'Destino',
    fee: money(fee.amount, fee.currency || input.currency),
  };
}

function whoOf(item) {
  const label = item.preparedBy && item.preparedBy.label;
  return (label && String(label).trim()) || 'Tu agente';
}

export default function PendientesAgente({ active = true }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);
  const [local, setLocal] = useState({});

  const cargar = useCallback(() => {
    if (!active) return;
    GATEWAY.get('/agent/v1/me/pending')
      .then((r) => setItems(r.data.pending || []))
      .catch(() => {});
  }, [active]);

  useEffect(() => {
    cargar();
    if (!active) return undefined;
    const id = setInterval(cargar, 15000);
    return () => clearInterval(id);
  }, [active, cargar]);

  const onConfirm = async (item) => {
    setBusy(`${item.tradeToken}:confirm`);
    setLocal((s) => ({ ...s, [item.tradeToken]: null }));
    try {
      await GATEWAY.post(`/agent/v1/me/pending/${item.tradeToken}/confirm`);
      setLocal((s) => ({
        ...s,
        [item.tradeToken]: {
          status: 'confirmed',
          message: 'Confirmado. Queda pendiente: execute todavía no existe, así que no se mueve dinero.',
        },
      }));
      cargar();
    } catch (e) {
      setLocal((s) => ({
        ...s,
        [item.tradeToken]: {
          status: 'error',
          message: (e && e.response && e.response.data && e.response.data.message) || 'No se pudo confirmar. Intenta de nuevo.',
        },
      }));
    } finally {
      setBusy(null);
    }
  };

  const onDismiss = async (item) => {
    setBusy(`${item.tradeToken}:dismiss`);
    setLocal((s) => ({ ...s, [item.tradeToken]: null }));
    try {
      await GATEWAY.post(`/agent/v1/me/pending/${item.tradeToken}/dismiss`);
      setItems((prev) => prev.filter((x) => x.tradeToken !== item.tradeToken));
      setLocal((s) => {
        const next = { ...s };
        delete next[item.tradeToken];
        return next;
      });
    } catch (e) {
      setLocal((s) => ({
        ...s,
        [item.tradeToken]: {
          status: 'error',
          message: (e && e.response && e.response.data && e.response.data.message) || 'No se pudo descartar. Intenta de nuevo.',
        },
      }));
    } finally {
      setBusy(null);
    }
  };

  if (!active) return null;

  const visibles = items.filter((it) => !it.cancelledAt);
  if (!visibles.length) return null;

  return (
    <div className="mt-6 space-y-3">
      {visibles.map((item) => {
        const d = detailsOf(item);
        const loc = local[item.tradeToken];
        const confirmed = item.verified || (loc && loc.status === 'confirmed');
        const who = whoOf(item);
        return (
          <div key={item.tradeToken} className="rounded-2xl border border-gray-200 bg-white p-6">
            <p className="text-lg font-semibold text-[#0a2540] tracking-tight">
              {who} preparó un {d.kindLabel}
            </p>
            {item.motivo && (
              <p className="mt-2 text-sm text-[#0a2540] bg-[#E6EBF7] rounded-lg px-3 py-2">
                <span className="text-gray-500">Su porqué: </span>«{item.motivo}»
              </p>
            )}
            <dl className="mt-3 space-y-1.5 text-sm">
              <div className="flex justify-between gap-3">
                <dt className="text-gray-500">Monto</dt>
                <dd className="font-medium text-gray-900 tabular-nums">{d.monto}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-gray-500">{d.destinoLabel}</dt>
                <dd className="font-medium text-gray-900">{d.destino}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-gray-500">Fee</dt>
                <dd className="font-medium text-gray-900 tabular-nums">{d.fee}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-gray-500">Expira</dt>
                <dd className="text-gray-700">{fmtFecha(item.expiresAt)}</dd>
              </div>
            </dl>
            <p className="mt-3 text-sm text-gray-600">
              Nada se mueve todavía: execute no existe. Si confirmas, queda pendiente hasta que exista execute.
            </p>
            {loc && loc.status === 'error' && (
              <p className="mt-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-2.5 py-1.5">
                {loc.message}
              </p>
            )}
            {confirmed ? (
              <p className="mt-3 text-sm text-gray-800 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                {(loc && loc.message) || 'Confirmado. Queda pendiente: execute todavía no existe, así que no se mueve dinero.'}
              </p>
            ) : (
              <div className="mt-4 flex gap-2 flex-wrap">
                <button
                  type="button"
                  disabled={Boolean(busy)}
                  onClick={() => onConfirm(item)}
                  className="text-sm px-4 py-2 rounded-lg bg-[#2A59C6] text-white disabled:opacity-50"
                >
                  {busy === `${item.tradeToken}:confirm` ? 'Confirmando…' : 'Confirmar'}
                </button>
                <button
                  type="button"
                  disabled={Boolean(busy)}
                  onClick={() => onDismiss(item)}
                  className="text-sm px-4 py-2 rounded-lg border border-gray-300 text-gray-700 disabled:opacity-50"
                >
                  {busy === `${item.tradeToken}:dismiss` ? 'Descartando…' : 'Descartar'}
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
